import InvoiceStateBadge from '@/components/invoices/InvoiceStateBadge'
import { Invoice, InvoiceWithContract } from '@/types'
import { formatCurrency } from '@/utils/format'
import { ActionIcon, Group, Table, Text, Tooltip } from '@mantine/core'
import { IconArrowBackUp, IconCash, IconFileTypePdf, IconSend } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MouseEvent } from 'react'

export default function InvoiceRow({
  invoice,
  onSelect,
  onPay,
  onResend,
  onCancelAndRedo,
  onOpenPdf,
}: {
  invoice: InvoiceWithContract
  onSelect: (invoice: Invoice) => void
  onPay: (invoice: Invoice) => void
  onResend: (invoice: Invoice) => void
  onCancelAndRedo: (invoice: Invoice) => void
  onOpenPdf: (invoice: Invoice) => void
}) {
  // The row opens the invoice, so its actions must not bubble up to it.
  const stopping = (action: (invoice: Invoice) => void) => (event: MouseEvent) => {
    event.stopPropagation()
    action(invoice)
  }

  const handleClick = () => onSelect(invoice)
  const handlePay = stopping(onPay)
  const handleResend = stopping(onResend)
  const handleCancelAndRedo = stopping(onCancelAndRedo)
  const handleOpenPdf = stopping(onOpenPdf)

  const period = invoice.period_start ? dayjs(invoice.period_start).format('MMMM YYYY') : '—'

  return (
    <Table.Tr
      style={{ cursor: 'pointer' }}
      bg={invoice.overdue ? 'var(--mantine-color-orange-0)' : undefined}
      onClick={handleClick}
    >
      <Table.Td fw={600} style={{ whiteSpace: 'nowrap' }}>
        {invoice.number}
      </Table.Td>
      <Table.Td>{invoice.contract.customer_name}</Table.Td>
      <Table.Td tt="capitalize" style={{ whiteSpace: 'nowrap' }}>
        {period}
      </Table.Td>
      <Table.Td>{dayjs(invoice.issued_on).format('DD/MM/YYYY')}</Table.Td>
      <Table.Td>
        <Text size="sm" c={invoice.overdue ? 'orange.8' : undefined} fw={invoice.overdue ? 600 : undefined}>
          {invoice.due_on ? dayjs(invoice.due_on).format('DD/MM/YYYY') : '—'}
        </Text>
      </Table.Td>
      <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
        {formatCurrency(invoice.total_including_tax)}
      </Table.Td>
      <Table.Td ta="right" style={{ whiteSpace: 'nowrap' }}>
        {invoice.cancelled_at ? '—' : formatCurrency(invoice.amount_due)}
      </Table.Td>
      <Table.Td>
        <InvoiceStateBadge state={invoice.state} />
      </Table.Td>
      <Table.Td>
        <Group gap={6} justify="flex-end" wrap="nowrap">
          {invoice.payable && (
            <Tooltip label="Marquer payé" withArrow>
              <ActionIcon variant="light" color="green" size={32} onClick={handlePay} aria-label="Marquer payé">
                <IconCash size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {invoice.resendable && (
            <Tooltip label="Renvoyer" withArrow>
              <ActionIcon variant="light" size={32} onClick={handleResend} aria-label="Renvoyer">
                <IconSend size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {invoice.cancellable && (
            <Tooltip label="Annuler et refaire" withArrow>
              <ActionIcon
                variant="light"
                color="red"
                size={32}
                onClick={handleCancelAndRedo}
                aria-label="Annuler et refaire"
              >
                <IconArrowBackUp size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {invoice.platform_invoice_id && (
            <Tooltip label="PDF" withArrow>
              <ActionIcon variant="default" size={32} onClick={handleOpenPdf} aria-label="PDF">
                <IconFileTypePdf size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  )
}
